import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  Icon,
  SegmentedRadioGroup,
  Select,
  Text,
  TextInput,
} from '@gravity-ui/uikit';
import { ArrowRight, Envelope, Pencil } from '@gravity-ui/icons';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorState, LoadingState } from '@/shared/ui/states';
import {
  useCreateEmployee,
  useDeleteEmployee,
  useEmployeeCard,
  useResendInvitation,
  useStaffRoles,
  useUpdateEmployee,
} from './api';
import { EMPTY_DOCUMENTS, type Employee, type EmployeeDocuments } from './types';
import { toWorkEmail, WORK_EMAIL_DOMAIN, workEmailLocalPart } from './work-email';
import './staff-dialogs.css';

/** Общая форма обоих шагов и обеих вкладок. */
interface EmployeeForm {
  firstName: string;
  lastName: string;
  email: string;
  orgEmail: string;
  roleId: string;
  documents: EmployeeDocuments;
}

const EMPTY: EmployeeForm = {
  firstName: '',
  lastName: '',
  email: '',
  orgEmail: '',
  roleId: '',
  documents: EMPTY_DOCUMENTS,
};

/**
 * Первый шаг обязателен целиком: без имени, почты и роли сотрудника не
 * завести. Документы — нет: карточку заводят раньше, чем приносят паспорт,
 * и бэкенд пустые поля принимает.
 */
function isProfileReady(form: EmployeeForm): boolean {
  return (
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.email.trim() !== '' &&
    form.roleId !== ''
  );
}

/** Поля профиля — первый шаг мастера и вкладка «Общая информация». */
function ProfileFields({
  form,
  onChange,
  withOrgEmail = true,
  showPasswordNote = true,
}: {
  form: EmployeeForm;
  onChange: (patch: Partial<EmployeeForm>) => void;
  withOrgEmail?: boolean;
  showPasswordNote?: boolean;
}) {
  const { t } = useTranslation();
  const roles = useStaffRoles();

  return (
    <>
      <Flex gap={2}>
        <TextInput
          size="l"
          label={t('staff.firstNameLabel')}
          placeholder={t('staff.firstNamePlaceholder')}
          value={form.firstName}
          onUpdate={(v) => onChange({ firstName: v })}
          autoFocus
          style={{ flex: 1 }}
        />
        <TextInput
          size="l"
          label={t('staff.lastNameLabel')}
          placeholder={t('staff.lastNamePlaceholder')}
          value={form.lastName}
          onUpdate={(v) => onChange({ lastName: v })}
          style={{ flex: 1 }}
        />
      </Flex>
      <TextInput
        size="l"
        label={t('staff.emailLabel')}
        placeholder="example@mail.ru"
        type="email"
        value={form.email}
        onUpdate={(v) => onChange({ email: v })}
        note={
          showPasswordNote ? (
            <Text variant="caption-2" color="secondary" style={{ textAlign: 'left' }}>
              {t('staff.emailNote')}
            </Text>
          ) : undefined
        }
      />
      <Select
        size="l"
        label={t('staff.roleLabel')}
        placeholder={t('staff.rolePlaceholder')}
        width="max"
        value={form.roleId ? [form.roleId] : []}
        onUpdate={(v) => onChange({ roleId: v[0] ?? '' })}
        options={(roles.data?.roles ?? []).map((role) => ({
          value: role.id,
          content: role.name,
        }))}
      />
      {/* Домен фиксирован, вводится только локальная часть — виджет из
          макета. Поле необязательное и ни на что не влияет: приглашение
          уходит на почту выше, а сюда пишут корпоративный адрес, если он
          вообще есть. */}
      {withOrgEmail && (
        <TextInput
          size="l"
          label={t('staff.orgEmailLabel')}
          placeholder="i.ivanov"
          value={workEmailLocalPart(form.orgEmail)}
          onUpdate={(v) => onChange({ orgEmail: toWorkEmail(v) })}
          endContent={
            <Text variant="body-1" color="secondary">
              {WORK_EMAIL_DOMAIN}
            </Text>
          }
        />
      )}
    </>
  );
}

/** Документы — второй шаг мастера и вкладка «Документы». */
function DocumentFields({
  documents,
  onChange,
  showOtherDoc = true,
}: {
  documents: EmployeeDocuments;
  onChange: (patch: Partial<EmployeeDocuments>) => void;
  showOtherDoc?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Flex gap={2}>
        <TextInput
          size="l"
          label={t('staff.snilsLabel')}
          placeholder="112-233-445 95"
          value={maskSnils(documents.snils)}
          onUpdate={(v) => onChange({ snils: digitsOnly(v, 11) })}
          autoFocus
          style={{ flex: 1 }}
        />
        <TextInput
          size="l"
          label={t('staff.innLabel')}
          placeholder={t('staff.innPlaceholder')}
          value={documents.inn}
          onUpdate={(v) => onChange({ inn: digitsOnly(v, 12) })}
          style={{ flex: 1 }}
        />
      </Flex>
      {/* Паспорт одним полем с маской; серия и номер хранятся раздельно
          (ведущий ноль серии значащий) и разделяются на лету. */}
      <TextInput
        size="l"
        label={t('staff.passportLabel')}
        placeholder="0123 456789"
        value={maskPassport(documents.passportSeries, documents.passportNumber)}
        onUpdate={(v) => {
          const raw = digitsOnly(v, 10);
          onChange({ passportSeries: raw.slice(0, 4), passportNumber: raw.slice(4) });
        }}
      />
      {showOtherDoc && (
        <TextInput
          size="l"
          label={t('staff.otherDocLabel')}
          placeholder={t('staff.otherDocPlaceholder')}
          value={documents.otherDoc}
          onUpdate={(v) => onChange({ otherDoc: v })}
        />
      )}
    </>
  );
}

/** Оставляет только цифры и обрезает до нужной длины. */
function digitsOnly(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

/** СНИЛС в виде XXX-XXX-XXX XX: показ, в значении остаются только цифры. */
function maskSnils(value: string): string {
  const raw = digitsOnly(value, 11);
  const part1 = raw.slice(0, 3);
  const part2 = raw.slice(3, 6);
  const part3 = raw.slice(6, 9);
  const part4 = raw.slice(9, 11);
  const chunks = [part1, part2, part3].filter(Boolean).join('-');
  return part4 ? `${chunks} ${part4}` : chunks;
}

/** Паспорт одним полем «серия номер»: показ, в значении — два раздельных поля. */
function maskPassport(series: string, number: string): string {
  const raw = `${digitsOnly(series, 4)}${digitsOnly(number, 6)}`.slice(0, 10);
  const part1 = raw.slice(0, 4);
  const part2 = raw.slice(4);
  return part1 && part2 ? `${part1} ${part2}` : part1;
}

/** Двухшаговое добавление сотрудника из макета. */
export function AddEmployeeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateEmployee();
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState(EMPTY);

  const set = (patch: Partial<EmployeeForm>) => setForm((value) => ({ ...value, ...patch }));
  const setDoc = (patch: Partial<EmployeeDocuments>) =>
    setForm((value) => ({ ...value, documents: { ...value.documents, ...patch } }));

  const close = () => {
    setStep(1);
    setForm(EMPTY);
    onClose();
  };

  // Тосты об итоге живут в useCreateEmployee — здесь только судьба диалога.
  // При ошибке он остаётся открытым намеренно: карточка могла создаться,
  // а письмо не уйти, и человек должен увидеть, что пошло не так.
  const submit = () => {
    create.mutate(form, { onSuccess: close });
  };

  return (
    <Dialog open={open} onClose={close} size="s">
      <Dialog.Header caption={t('staff.addTitle')} />
      <Dialog.Divider />
      <Dialog.Body>
        <Flex direction="column" gap={3}>
          <Text variant="body-1" color="secondary">
            {t('staff.addStep', { step })}
          </Text>
          {step === 1 ? (
            <ProfileFields form={form} onChange={set} withOrgEmail={false} />
          ) : (
            <DocumentFields documents={form.documents} onChange={setDoc} />
          )}
        </Flex>
      </Dialog.Body>
      <Dialog.Divider />
      <Dialog.Footer>
        <Flex justifyContent="flex-end" gap={2} style={{ width: '100%' }}>
          <Button view="flat" size="l" onClick={step === 1 ? close : () => setStep(1)}>
            {step === 1 ? t('common.cancel') : t('staff.back')}
          </Button>
          <Button
            view="action"
            size="l"
            onClick={step === 1 ? () => setStep(2) : submit}
            disabled={step === 1 && !isProfileReady(form)}
            loading={create.isPending}
          >
            <Icon data={step === 1 ? ArrowRight : Envelope} size={16} />
            {step === 1 ? t('staff.next') : t('staff.addAndInvite')}
          </Button>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
}

type EditTab = 'general' | 'documents';

/**
 * Редактирование сотрудника — вкладки как в макете.
 *
 * Карточка догружается целиком: в списке документов нет, они приезжают
 * только по GetEmployee. Одним запросом на обе вкладки, поэтому при
 * переключении вкладки ничего не подгружается.
 */
export function EditEmployeeDialog({
  employee,
  onClose,
}: {
  employee: Employee | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const card = useEmployeeCard(employee?.id ?? null);
  const update = useUpdateEmployee();
  const [tab, setTab] = useState<EditTab>('general');
  const [form, setForm] = useState(EMPTY);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!card.data) return;
    setForm({
      firstName: card.data.employee.firstName,
      lastName: card.data.employee.lastName,
      email: card.data.employee.email,
      orgEmail: card.data.employee.orgEmail,
      roleId: card.data.employee.roleId,
      documents: card.data.documents,
    });
  }, [card.data]);

  // Animate dialog body height when tab or content changes for smooth resize.
  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    // Measure previous and next heights and animate between them.
    const prevHeight = outer.clientHeight || inner.scrollHeight;
    const nextHeight = inner.scrollHeight;

    // Set to previous explicit height, then to new height to trigger transition.
    outer.style.height = `${prevHeight}px`;
    // Force reflow
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    outer.offsetHeight;
    outer.style.transition = 'height 180ms ease';
    outer.style.height = `${nextHeight}px`;

    const onEnd = () => {
      // Allow natural height after transition.
      outer.style.height = 'auto';
      outer.style.transition = '';
      outer.removeEventListener('transitionend', onEnd);
    };
    outer.addEventListener('transitionend', onEnd);
  }, [tab, card.data, employee]);

  useEffect(() => {
    if (employee) setTab('general');
  }, [employee]);

  if (!employee) return null;

  const set = (patch: Partial<EmployeeForm>) => setForm((value) => ({ ...value, ...patch }));
  const setDoc = (patch: Partial<EmployeeDocuments>) =>
    setForm((value) => ({ ...value, documents: { ...value.documents, ...patch } }));

  const close = () => {
    setTab('general');
    setForm(EMPTY);
    onClose();
  };

  const submit = () => {
    update.mutate({ id: employee.id, patch: form }, { onSuccess: close });
  };

  return (
    <Dialog open onClose={close} size="s">
      <Dialog.Header caption={t('staff.editTitle')} />
      <Dialog.Divider />
      <div style={{ padding: '12px 16px 0' }}>
        <SegmentedRadioGroup
          className="employee-edit-tabs"
          size="l"
          width="max"
          value={tab}
          onUpdate={(value) => setTab(value as EditTab)}
        >
          <SegmentedRadioGroup.Option value="general" content={t('staff.tabGeneral')} />
          <SegmentedRadioGroup.Option value="documents" content={t('staff.tabDocuments')} />
        </SegmentedRadioGroup>
        {/* Линия по ширине сегмента, не на всю ширину диалога. */}
        <div style={{ marginTop: 12, borderBottom: '1px solid var(--g-color-line-generic)' }} />
      </div>
      <Dialog.Body>
        <div style={{ overflow: 'hidden' }} ref={outerRef}>
          <div ref={innerRef}>
            <Flex direction="column" gap={3}>
              {card.isPending && <LoadingState />}
              {card.isError && (
                <ErrorState error={card.error} onRetry={() => void card.refetch()} />
              )}
              {card.data &&
                (tab === 'general' ? (
                  <ProfileFields
                    form={form}
                    onChange={set}
                    withOrgEmail={false}
                    showPasswordNote={false}
                  />
                ) : (
                  <DocumentFields
                    documents={form.documents}
                    onChange={setDoc}
                    showOtherDoc={false}
                  />
                ))}
            </Flex>
          </div>
        </div>
      </Dialog.Body>
      <Dialog.Divider />
      <Dialog.Footer>
        <Flex justifyContent="flex-end" gap={2} style={{ width: '100%' }}>
          <Button view="flat" size="l" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            view="action"
            size="l"
            onClick={submit}
            disabled={!card.data || !isProfileReady(form)}
            loading={update.isPending}
          >
            <Icon data={Pencil} size={16} />
            {t('staff.change')}
          </Button>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
}

/** Подтверждение удаления сотрудника. */
export function DeleteEmployeeDialog({
  employee,
  onClose,
}: {
  employee: Employee | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteEmployee();

  if (!employee) return null;

  const submit = () => {
    remove.mutate(employee.id, { onSuccess: onClose });
  };

  return (
    <Dialog open onClose={onClose} size="s">
      <Dialog.Header caption={t('staff.deleteTitle')} />
      <Dialog.Body>
        <Text variant="body-2">
          {t('staff.deleteBody', {
            name: `${employee.lastName} ${employee.firstName}`.trim(),
          })}
        </Text>
      </Dialog.Body>
      <Dialog.Footer
        onClickButtonCancel={onClose}
        onClickButtonApply={submit}
        textButtonCancel={t('common.cancel')}
        textButtonApply={t('common.delete')}
        propsButtonApply={{ view: 'outlined-danger' }}
        loading={remove.isPending}
      />
    </Dialog>
  );
}

/**
 * Повторная отправка приглашения — на месте «Сброса пароля» из макета.
 *
 * Паролей сервис не выдаёт и не хранит: человек задаёт его сам при
 * регистрации в Logto. Поменять адрес прямо здесь нельзя намеренно —
 * приглашение уходит на ту почту, что записана в карточке, а поправить её
 * можно в редактировании, где изменение сохранится.
 */
export function ResendInvitationDialog({
  employee,
  onClose,
}: {
  employee: Employee | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const resend = useResendInvitation();

  if (!employee) return null;

  const fullName = `${employee.firstName} ${employee.lastName}`.trim();

  const submit = () => {
    resend.mutate(employee.id, { onSuccess: onClose });
  };

  return (
    <Dialog open onClose={onClose} size="s">
      <Dialog.Header caption={t('staff.resendTitle')} />
      <Dialog.Divider />
      <Dialog.Body>
        <Text variant="body-2">
          <Trans
            i18nKey="staff.resendBody"
            values={{ name: fullName, email: employee.email }}
            components={{ bold: <Text variant="body-2" style={{ fontWeight: 600 }} /> }}
          />
        </Text>
      </Dialog.Body>
      <Dialog.Divider />
      <Dialog.Footer>
        <Flex justifyContent="flex-end" gap={2} style={{ width: '100%' }}>
          <Button view="flat" size="l" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button view="action" size="l" onClick={submit} loading={resend.isPending}>
            <Icon data={Envelope} size={16} />
            {t('staff.send')}
          </Button>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
}
